import { useHandler } from "@chatbotgang/etude/react/useHandler";
import { css } from "@emotion/react";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import type { SxProps } from "@mui/material/styles";
import TextField from "@mui/material/TextField";
import type { Overwrite } from "@mui/types";
import { GOOGLE_ADS_SERVICE_ACCOUNT } from "@polifonia/env";
import type { muiTheme } from "@polifonia/theme";
import { theme } from "@polifonia/theme";
import type { SubmitHandler } from "react-hook-form";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/Button";
import { CopyButton } from "@/components/Button/CopyButton";
import { ExternalLink } from "@/components/ExternalLink";
import type { ModalProps } from "@/components/Modal";
import { Modal } from "@/components/Modal";
import { Typography } from "@/components/Typography";

namespace ConnectGoogleAdsManagerAccountModal {
  export interface OwnProps {
    open: boolean;
    onClose: () => void;
    onConnect: (customerId: string) => void;
  }

  export type Props = Overwrite<
    Omit<ModalProps, "children" | "handleClose" | "title">,
    OwnProps
  >;
}

const cssContent = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const cssInstructions = css`
  margin: 0;
  padding-left: 21px;
  color: ${theme.colors.staticFgNote};
  font-size: 0.875rem;
  line-height: 20px;

  li {
    margin-bottom: 0;
  }
`;

const grantAccessStyles = {
  root: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    padding: "12px",
    borderRadius: "8px",
    backgroundColor: theme.colors.staticBgContainer,
  },
  valueRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "8px",
    wordBreak: "break-all",
  },
} satisfies Record<string, SxProps<typeof muiTheme>>;

/**
 * Connecting fails with GOOGLE_ADS_ACCESS_NOT_GRANTED until the manager
 * account lets in Crescendo Lab's service account, which is the most common
 * reason this modal fails. Spell out both settings with the values to paste,
 * instead of only pointing at the help article.
 */
const GrantAccessSteps: React.FC = function GrantAccessSteps() {
  const { t } = useTranslation();
  if (!GOOGLE_ADS_SERVICE_ACCOUNT) return null;
  const domain = GOOGLE_ADS_SERVICE_ACCOUNT.split("@")[1] ?? "";

  return (
    <Box sx={grantAccessStyles.root}>
      <Typography variant="body" fontWeight={500}>
        {t(
          "applications.googleAds.connectModal.grantAccessTitle",
          "First, let Crescendo Lab access your manager account in Google Ads",
        )}
      </Typography>
      <Typography variant="body">
        {t(
          "applications.googleAds.connectModal.grantDomainStep",
          "1. In Settings › Access and security › Security, allow this domain:",
        )}
      </Typography>
      <Box sx={grantAccessStyles.valueRow}>
        <Typography variant="body" fontWeight={500}>
          {domain}
        </Typography>
        <CopyButton text={domain} variant="plain" />
      </Box>
      <Typography variant="body">
        {t(
          "applications.googleAds.connectModal.grantUserStep",
          "2. On the Users tab, add this account with Standard access:",
        )}
      </Typography>
      <Box sx={grantAccessStyles.valueRow}>
        <Typography variant="body" fontWeight={500}>
          {GOOGLE_ADS_SERVICE_ACCOUNT}
        </Typography>
        <CopyButton text={GOOGLE_ADS_SERVICE_ACCOUNT} variant="plain" />
      </Box>
    </Box>
  );
};

interface FormValues {
  customerId: string;
}

// Remove all non-digit characters (including hidden characters from copy-paste)
const sanitizeCustomerId = (value: string): string => {
  return value.replace(/\D/g, "");
};

export const ConnectGoogleAdsManagerAccountModal: React.FC<
  ConnectGoogleAdsManagerAccountModal.Props
> = ({ open, onClose, onConnect }) => {
  const { t } = useTranslation();
  const { control, handleSubmit } = useForm<FormValues>({
    mode: "onChange",
    defaultValues: {
      customerId: "",
    },
  });

  const onSubmit = useHandler<SubmitHandler<FormValues>>((values) => {
    onConnect(sanitizeCustomerId(values.customerId));
    onClose();
  });

  const handleModalClose = useHandler<ModalProps["handleClose"]>(
    (_event, reason) => {
      if (reason === "backdropClick") {
        return;
      }
      onClose();
    },
  );

  return (
    <Modal
      open={open}
      handleClose={handleModalClose}
      title={t(
        "applications.googleAds.connectGoogleAdsManagerAccountModal.title",
      )}
      footer={
        <Stack direction="row" justifyContent="flex-end" gap="12px">
          <Button variant="plain" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button variant="primary" onClick={handleSubmit(onSubmit)}>
            {t("common.connect")}
          </Button>
        </Stack>
      }
    >
      <Stack gap="8px" css={cssContent}>
        <GrantAccessSteps />
        <Typography
          variant="body"
          fontWeight={500}
          color={theme.colors.staticFgBody}
        >
          {t(
            "applications.googleAds.connectGoogleAdsManagerAccountModal.customerIdTitle",
          )}
        </Typography>
        <ul css={cssInstructions}>
          <li>
            {t(
              "applications.googleAds.connectGoogleAdsManagerAccountModal.instruction1",
            )}
          </li>
          <li>
            {t(
              "applications.googleAds.connectGoogleAdsManagerAccountModal.instruction2",
            )}
          </li>
        </ul>
        <ExternalLink
          href={t(
            "applications.googleAds.connectGoogleAdsManagerAccountModal.learnMore.link",
          )}
          trailingIcon={true}
        >
          {t("common.learnMore")}
        </ExternalLink>
        <Controller
          name="customerId"
          control={control}
          rules={{
            validate: (value) => {
              const sanitizedValue = sanitizeCustomerId(value);
              if (sanitizedValue.length !== 10) {
                return t(
                  "applications.googleAds.connectGoogleAdsManagerAccountModal.validationError",
                );
              }
              return true;
            },
          }}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              autoComplete="off"
              placeholder={t(
                "applications.googleAds.connectGoogleAdsManagerAccountModal.placeholder",
              )}
              error={!!error}
              helperText={error?.message}
              fullWidth
            />
          )}
        />
      </Stack>
    </Modal>
  );
};
